import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import { useTickets } from 'contexts/TicketsContext';
import { useUserProfile } from 'contexts/UserProfileContext';
import { RequestQueryKeysE } from 'declarations/enums/react-query';
import type { ApiErrorI } from 'declarations/interfaces/errors';
import type { NoopT } from 'declarations/types/common';
import type { SelectOptionT } from 'declarations/types/select';
import { getErrorMessage, getValidationErrorMessage } from 'helpers/apiError';
import { useFetchAgentsList } from 'hooks/api/useTicketAgentsList';
import { useShareTickets } from 'hooks/api/useTickets';
import { updateSnackbarNotification } from 'reduxStore/features/notifications/notificationsSlice';
import { useAppDispatch } from 'reduxStore/hooks';

import type { ShareTicketFormValuesT } from '../../ShareTicketPopup/declarations';

type UseShareTicketPopupT = {
  isLoadingAgentOptions: boolean;
  agentIdsOptions: SelectOptionT[];
  isPopupOpened: boolean;
  handleSubmitForm: (data: ShareTicketFormValuesT, onClose: NoopT | undefined) => void;
  isSharingTicket: boolean;
  handleCloseShareTicketPopup: (onClosePopupCb: NoopT | undefined) => NoopT;
  handleAgentChange: (roofTopId: string) => void;
};

const useShareTicketPopup = (): UseShareTicketPopupT => {
  /**
   * Hooks
   */
  const {
    setShareTicketPopupVisibility,
    isShareTicketPopupOpened,
    selectedTicketsIds,
    selectedTicketsRooftopIds,
    setSelectedTicketsIds,
    selectedTicketId,
  } = useTickets();
  const [, setSelectedAgentId] = useState<string | undefined>(undefined);
  const { isLoadingUserProfile } = useUserProfile();
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();

  /**
   * Api hooks
   */
  const shareTicketMutation = useShareTickets({
    onSuccess: () => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketMessages]);
    },
  });

  const ticketUseFetchAgentsListResult = useFetchAgentsList(
    isShareTicketPopupOpened,
    selectedTicketsRooftopIds
  );

  /**
   * Variables
   */
  const agentIdsOptions = useMemo(
    () =>
      ticketUseFetchAgentsListResult.data
        ? ticketUseFetchAgentsListResult.data
            .map(({ userId, displayName }) => ({ label: displayName, value: userId }))
            .sort((a, b) => {
              if (a.label > b.label) {
                return 1;
              }

              if (a.label < b.label) {
                return -1;
              }

              return 0;
            })
        : [],
    [ticketUseFetchAgentsListResult.data]
  );

  /**
   * Handlers
   */
  const handleCloseShareTicketPopup = useCallback(
    (onClosePopupCb: NoopT | undefined) => () => {
      onClosePopupCb ? onClosePopupCb() : setShareTicketPopupVisibility(false);
      setSelectedTicketsIds([]);
    },
    [setShareTicketPopupVisibility, setSelectedTicketsIds]
  );

  const handleSubmitForm = useCallback(
    (data: ShareTicketFormValuesT, onClose: NoopT | undefined) => {
      shareTicketMutation.mutate(
        {
          ...data,
          ticketIds: selectedTicketId ? [selectedTicketId] : selectedTicketsIds,
        },
        {
          onSuccess: () => {
            dispatch(
              updateSnackbarNotification({
                snackbarMessage: 'Ticket successfully shared',
                snackBarSeverity: 'success',
              })
            );
            onClose ? onClose() : setShareTicketPopupVisibility(false);
            setSelectedTicketsIds([]);
          },
          onError: error => {
            const errorMsg =
              getErrorMessage(error as ApiErrorI) || getValidationErrorMessage(error as ApiErrorI);
            dispatch(
              updateSnackbarNotification({
                snackbarMessage: `Something went wrong. ${errorMsg || ''}`,
                snackBarSeverity: 'error',
              })
            );
            onClose ? onClose() : setShareTicketPopupVisibility(false);
            setSelectedTicketsIds([]);
          },
        }
      );
    },
    [
      shareTicketMutation,
      selectedTicketsIds,
      selectedTicketId,
      dispatch,
      setShareTicketPopupVisibility,
      setSelectedTicketsIds,
    ]
  );

  const handleAgentChange: (agentId: string) => void = (agentId: string): void => {
    setSelectedAgentId(agentId);
  };

  return {
    isLoadingAgentOptions: isLoadingUserProfile,
    agentIdsOptions,
    handleCloseShareTicketPopup,
    isPopupOpened: isShareTicketPopupOpened,
    handleSubmitForm,
    isSharingTicket: shareTicketMutation.isLoading,
    handleAgentChange,
  };
};

export default useShareTicketPopup;
